import React from "react";
import DOMPurify from "dompurify";
import { UserTypes } from "shared-types";
import { withTranslation, WithTranslation } from "i18n";
import {
  AboutMe,
  BasicInfo,
  CreditsList,
  FacetCategory,
  IAgency,
  IAgencyList,
  ImageLocation,
  Photo,
  Skill,
  Training,
} from "models";
import { SocialMedia } from "models/Social";

import { getImageUrl } from "services/UrlBuilder";

import { TYPE_AVAILABLE_FOR } from "pages/MainPage/MainPage";

import { MainPhoto } from "components/common/MainPhoto";

import { AgencyDetails, IAgencyDetailsContent } from "../AgencyDetails";
import { GET_BASIC_INFO_DETAILS_LIST } from "../BasicInfoSection";
import { FurtherInformation } from "../FurtherInformation";
import { SkillsSection } from "../SkillsSection";

import {
  BasicInfoDetails,
  CVNotes,
  MediaLinks,
  PerformerCreditsGrid,
  PerformerNameBar,
} from "./components";
import { IPaperCVContent } from "./PaperCVContent";

import "./PaperCV.scss";
import compose from "shared/utils/compose";
import {
  LaunchDarklyClientWithFlags,
  withLaunchDarklyConsumerHOC,
} from "shared-services";

const EMPTY_FUNCTION = () => undefined;

interface IPaperCVComposeProps {
  cvNotes?: string;
  userType: UserTypes;
  userTypeLabel: string;
  fullName?: string;
  mainPhoto?: Photo;
  socialMedia?: SocialMedia;
  basicInfo?: BasicInfo;
  facets: FacetCategory[];
  content: IPaperCVContent;
  galleryPhoto?: Photo[];
  creditsList?: CreditsList;
  skills?: Skill[];
  aboutMe?: AboutMe;
  training?: Training;
  agencies?: IAgencyList;
  isViewMode: boolean;
}

export interface IPaperCVProps
  extends IPaperCVComposeProps,
    LaunchDarklyClientWithFlags,
    WithTranslation {}

export class PaperCV extends React.Component<IPaperCVProps> {
  public render() {
    return (
      <div className="c-paper-cv">
        {this.renderNotes()}
        {this.renderPerformerNameBar()}
        <div className="c-paper-cv__profile">
          <div className="c-paper-cv__profile-links">
            {this.renderMainPhoto()}
            {this.renderMediaLinks()}
          </div>
          <div className="c-paper-cv__basic-info-section">
            {this.renderBasicInfoDetails()}
          </div>
        </div>
        {this.renderAgencies()}
        {this.renderGallery()}
        {this.renderAboutMe()}
        {this.renderPerformerCreditsGrid()}
        {this.renderSkills()}
        {this.renderTraining()}
        {this.renderFooter()}
      </div>
    );
  }

  private renderNotes = () => {
    const { content, cvNotes } = this.props;

    const isVisible = Boolean(cvNotes);

    return (
      isVisible && (
        <div className="c-paper-cv__notes">
          <CVNotes content={content} notes={cvNotes} />
        </div>
      )
    );
  };

  private renderPerformerNameBar = () => {
    const { userTypeLabel, fullName = "", userType } = this.props;

    const getBrandLogo = () => {
      if (userType === UserTypes.filmmakersProfile) {
        return "filmmakers";
      }

      return "spotlight";
    };

    const isTypeVisible = TYPE_AVAILABLE_FOR.some((type) => type === userType);
    const visibleUserType = isTypeVisible ? userTypeLabel : "";

    return (
      <div className="c-paper-cv__name-bar">
        <PerformerNameBar
          fullName={fullName}
          userType={visibleUserType}
          logo={getBrandLogo()}
        />
      </div>
    );
  };

  private renderMainPhoto = () => {
    const { mainPhoto } = this.props;

    return (
      mainPhoto && (
        <div className="c-paper-cv__profile-photo">
          <MainPhoto
            showError={false}
            photo={mainPhoto}
            content={{} as any}
            openPhoto={EMPTY_FUNCTION}
          />
        </div>
      )
    );
  };

  private renderMediaLinks = () => {
    const { socialMedia, content } = this.props;
    const hasAnyLink = socialMedia && socialMedia.website;

    return (
      socialMedia &&
      hasAnyLink && (
        <div className="c-paper-cv__social-media">
          <MediaLinks socialMedia={socialMedia} content={content} />
        </div>
      )
    );
  };

  private renderBasicInfoDetails = () => {
    const {
      basicInfo,
      facets,
      content: rawContent,
      isViewMode,
      flags,
      t,
    } = this.props;

    const content = {
      ...rawContent,
      basicInfoSection: {
        ...rawContent.basicInfoSection,
      },
    };

    const basicInfoDetails = basicInfo
      ? GET_BASIC_INFO_DETAILS_LIST(
          facets,
          basicInfo,
          content.basicInfoSection,
          isViewMode,
          t,
          flags
        )
      : [];

    return (
      basicInfo && (
        <React.Fragment>
          <div className="c-paper-cv__title dark">
            {content.profileSectionLabel}
          </div>
          <div className="c-paper-cv__basic-info">
            <BasicInfoDetails details={basicInfoDetails} />
          </div>
        </React.Fragment>
      )
    );
  };

  private renderAgencies = () => {
    const { agencies, content, userType } = this.props;
    const agenciesToRender: IAgency[] = [];

    if (agencies) {
      if (
        agencies.schools &&
        [UserTypes.femaleGraduates, UserTypes.maleGraduates].some(
          (type) => type === userType
        )
      ) {
        agenciesToRender.push(...agencies.schools);
      }

      agenciesToRender.push(...agencies.agents);
    }

    const isVisible = agenciesToRender.length > 0;
    const AgencyDetailsContent: IAgencyDetailsContent = {
      telephoneText: content.agencyDetailsTelephone,
      mobilePhoneText: content.agencyDetailsMobilePhone,
    };

    return (
      isVisible && (
        <div className="c-paper-cv__contacts">
          <div className="c-paper-cv__title">
            {content.contactsSectionLabel}
          </div>
          <div className="c-paper-cv__agencies">
            {agenciesToRender.map((agent) => (
              <AgencyDetails
                key={agent.refNumber}
                agency={agent}
                content={AgencyDetailsContent}
              />
            ))}
          </div>
        </div>
      )
    );
  };

  private renderGallery = () => {
    const { galleryPhoto = [], content } = this.props;

    const isVisible = galleryPhoto && galleryPhoto.length > 0;

    return (
      isVisible && (
        <div className="c-paper-cv__gallery-section">
          <div className="c-paper-cv__title dark">
            {content.gallerySectionLabel}
          </div>
          <div className="c-paper-cv__gallery">
            {galleryPhoto.map(({ id, url }) => (
              <img
                key={id}
                src={getImageUrl(url, ImageLocation.GalleryPhoto)}
                alt="Gallery"
              />
            ))}
          </div>
        </div>
      )
    );
  };

  private renderPerformerCreditsGrid = () => {
    const { creditsList = { credits: [], creditHeadings: [] }, content } =
      this.props;

    const isVisible = creditsList.credits.length > 0;

    return (
      isVisible && (
        <div className="c-paper-cv__credits-section">
          <div className="c-paper-cv__sub-title light">
            {content.creditsLabel}
          </div>
          <PerformerCreditsGrid content={content} creditsList={creditsList} />
        </div>
      )
    );
  };

  private renderSkills = () => {
    const { content, facets, skills = [] } = this.props;

    const isVisible = skills.length > 0;

    return (
      isVisible && (
        <div className="c-paper-cv__skills">
          <SkillsSection
            content={content.skillsSection}
            skillFacets={facets}
            skills={skills}
            editSkills={EMPTY_FUNCTION}
            isReadonly={true}
          />
        </div>
      )
    );
  };

  private renderTraining = () => {
    const { training = { notes: "" }, content } = this.props;

    const isVisible = Boolean(training.notes);

    return (
      isVisible && (
        <div className="c-paper-cv__training">
          <div className="c-paper-cv__sub-title light">
            {content.trainingLabel}
          </div>
          <FurtherInformation text={training.notes} />
        </div>
      )
    );
  };

  private renderAboutMe = () => {
    const { aboutMe = { currentAppearance: "" }, content } = this.props;

    const isVisible = Boolean(aboutMe.currentAppearance);

    return (
      isVisible && (
        <div className="c-paper-cv__about-me">
          <div className="c-paper-cv__sub-title light">
            {content.aboutMeLabel}
          </div>
          {aboutMe && <FurtherInformation text={aboutMe.currentAppearance} />}
        </div>
      )
    );
  };

  private renderFooter = () => (
    <div
      className="c-paper-cv__footer"
      dangerouslySetInnerHTML={{
        __html: DOMPurify.sanitize(this.props.content.footer || ""),
      }}
    />
  );
}

export default compose(
  withTranslation(),
  withLaunchDarklyConsumerHOC()
)(PaperCV) as React.ComponentType<IPaperCVComposeProps>;
